import styled from "@emotion/styled";
import { capitalize, isUndefined } from "lodash";
import firebase from "firebase/app";

import { useUserUsage } from "./useUserUsage";

type UserData = { email: string; roles?: string[]; groups?: string[] }
type SubscriptionUser = { email: string, tier: string }

type IUserListProps = {
    users: UserData[] | SubscriptionUser[];
    fbUser: firebase.User;
};

const TableContainer = styled.table`
    margin: 16px 0;
    table-layout: fixed;
    border-collapse: collapse;
    width: 100%;
    gap: 12px;
`;

const RowItem = styled.td`
    font-size: 14px;
    padding: 12px;
    text-overflow: ellipsis;
    overflow: hidden;
`;
const ColumnHeading = styled.th({
    fontSize: '16px',
    fontWeight: 'bold',
    padding: 12,
});

const RowContainer = styled.tr`
    border-bottom: 1px solid #ddd;
    margin-bottom: 12px;
    text-align: left;
`

export function UserList({ users, fbUser }: IUserListProps) {
    if (!users.length) return <></>;

    if (isSubscriptionUserArray(users)) {
        return <SubscriptionUserList users={users} fbUser={fbUser} />
    } else {
        return <UserDataList users={users} />
    }
}

function UserDataRow({ user }: {
    user: UserData
}) {
    return <RowContainer>
        <RowItem style={{ width: '40%' }}>{user.email}</RowItem>
        <RowItem>{user.roles?.join(", ")}</RowItem>
        <RowItem>{user.groups?.join(", ")}</RowItem>
    </RowContainer>
}

export function UserDataList({ users }: { users: UserData[] }) {
    return <TableContainer>
        <thead>
            <RowContainer>
                <ColumnHeading style={{ width: '40%' }}>Email</ColumnHeading>
                <ColumnHeading>Roles</ColumnHeading>
                <ColumnHeading>Groups</ColumnHeading>
            </RowContainer>
        </thead>
        <tbody>
            {users.map(u => (<UserDataRow key={u.email} user={u} />))}
        </tbody>
    </TableContainer >
}

export function SubscriptionUserList({ users, fbUser }: { users: SubscriptionUser[]; fbUser: firebase.User; }) {
    return <TableContainer>
        <thead>
            <RowContainer>
                <ColumnHeading style={{ width: '40%' }}>Email</ColumnHeading>
                <ColumnHeading>Tier</ColumnHeading>
                <ColumnHeading>Usage (hours this month)</ColumnHeading>
            </RowContainer>
        </thead>
        <tbody>
            {users.map(u => (<SubscriptionUserRow key={u.email} user={u} fbUser={fbUser} />))}
        </tbody>
    </TableContainer >
}

function SubscriptionUserRow({ user, fbUser }: {
    user: SubscriptionUser;
    fbUser: firebase.User;
}) {
    return <RowContainer>
        <RowItem style={{ width: '40%' }}>{user.email}</RowItem>
        <RowItem>{capitalize(user.tier)}</RowItem>
        <RowItem><UserUsage email={user.email} fbUser={fbUser} /></RowItem>
    </RowContainer>
}

const isSubscriptionUserArray = (users: UserData[] | SubscriptionUser[]): users is SubscriptionUser[] => {
    return 'tier' in users[0];
}

function UserUsage({ email, fbUser }: { email: string, fbUser: firebase.User; }) {
    const { sessionHoursUsed } = useUserUsage({ email, fbUser })

    return <>{isUndefined(sessionHoursUsed) ? 'loading' : Math.round((sessionHoursUsed) * 10) / 10}</>;
}